import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Bot, MoreHorizontal, Edit, Trash2, Search, Zap, ZapOff } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import useChatbotList from '../../hooks/useChatbotList';

function timeAgo(dateString) {
  const now = new Date();
  const date = new Date(dateString);
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days !== 1 ? 's' : ''} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
  const years = Math.floor(months / 12);
  return `${years} year${years !== 1 ? 's' : ''} ago`;
}

const statusStyles = {
  draft: 'bg-gray-100 text-gray-500',
  active: 'bg-emerald-50 text-emerald-700',
};

const statusLabels = {
  draft: 'Draft',
  active: 'Active',
};

function ActionsDropdown({ chatbot, onDelete, onToggleStatus }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(!open);
        }}
        className="p-1.5 rounded-md text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-colors"
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 w-44 origin-top-right rounded-lg bg-white shadow-lg border border-gray-100 py-1">
          <Link
            to={`/chatbots/${chatbot.id}/builder`}
            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            onClick={() => setOpen(false)}
          >
            <Edit className="h-3.5 w-3.5 mr-2.5 text-gray-400" />
            Edit
          </Link>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onToggleStatus(chatbot.id, chatbot.status === 'active' ? 'draft' : 'active');
              setOpen(false);
            }}
            className="flex items-center w-full px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {chatbot.status === 'active' ? (
              <>
                <ZapOff className="h-3.5 w-3.5 mr-2.5 text-gray-400" />
                Deactivate
              </>
            ) : (
              <>
                <Zap className="h-3.5 w-3.5 mr-2.5 text-emerald-500" />
                Activate
              </>
            )}
          </button>
          <div className="my-1 border-t border-gray-100" />
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(chatbot.id);
              setOpen(false);
            }}
            className="flex items-center w-full px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash2 className="h-3.5 w-3.5 mr-2.5" />
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

export default function ChatbotList() {
  const {
    chatbots,
    loading,
    error,
    showCreateModal,
    newChatbotName,
    creating,
    setShowCreateModal,
    setNewChatbotName,
    fetchChatbots,
    handleCreateChatbot,
    handleDeleteChatbot,
    handleToggleStatus,
  } = useChatbotList();

  const [searchQuery, setSearchQuery] = useState('');
  const modalRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        setShowCreateModal(false);
        setNewChatbotName('');
      }
    }
    if (showCreateModal) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateModal, setShowCreateModal, setNewChatbotName]);

  const filteredChatbots = chatbots.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="px-8 py-8 max-w-6xl mx-auto">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-8 w-32 bg-gray-100 rounded-lg animate-pulse" />
          <div className="ml-auto h-9 w-36 bg-gray-100 rounded-lg animate-pulse" />
        </div>
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-16 bg-gray-50 rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-sm text-red-500">{error}</p>
          <button
            onClick={fetchChatbots}
            className="mt-3 px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-8 py-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white tracking-tight">Chatbots</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium rounded-lg text-white bg-brand-600 hover:bg-brand-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          New Chatbot
        </button>
      </div>

      {chatbots.length === 0 ? (
        /* Empty state */
        <EmptyState
          illustration="chatbots"
          headline="Build your first AI chatbot"
          description="Create intelligent chatbots that engage visitors, answer questions, and capture leads on your website — powered by AI."
          buttonLabel="New Chatbot"
          onAction={() => setShowCreateModal(true)}
        />
      ) : (
        <>
          {/* Search bar */}
          <div className="mb-6">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search chatbots..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 transition-colors dark:text-white"
              />
            </div>
          </div>

          {/* Table */}
          <div>
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_100px_140px_44px] items-center px-4 pb-2 border-b border-gray-200 dark:border-gray-700">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Name</span>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Status</span>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Last Updated</span>
              <span className="sr-only">Actions</span>
            </div>

            {/* Rows */}
            {filteredChatbots.length === 0 ? (
              <div className="py-12 text-center text-sm text-gray-400">
                No chatbots matching "{searchQuery}"
              </div>
            ) : (
              <div>
                {filteredChatbots.map((chatbot) => (
                  <Link
                    key={chatbot.id}
                    to={`/chatbots/${chatbot.id}/builder`}
                    className="grid grid-cols-[1fr_100px_140px_44px] items-center px-4 py-3 border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors group"
                  >
                    {/* Name */}
                    <div className="min-w-0 flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: chatbot.branding?.primary_color ? `${chatbot.branding.primary_color}15` : '#2563eb15' }}
                      >
                        <Bot
                          className="w-4 h-4"
                          style={{ color: chatbot.branding?.primary_color || '#2563eb' }}
                        />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-white truncate group-hover:text-brand-600 transition-colors">
                          {chatbot.name}
                        </p>
                        {chatbot.description && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{chatbot.description}</p>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          statusStyles[chatbot.status] || statusStyles.draft
                        }`}
                      >
                        {statusLabels[chatbot.status] || 'Draft'}
                      </span>
                    </div>

                    {/* Last Updated */}
                    <div className="text-xs text-gray-400">
                      {timeAgo(chatbot.updated_at)}
                    </div>

                    {/* Actions */}
                    <div onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
                      <ActionsDropdown
                        chatbot={chatbot}
                        onDelete={handleDeleteChatbot}
                        onToggleStatus={handleToggleStatus}
                      />
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Create Chatbot Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div
            ref={modalRef}
            className="w-full max-w-sm mx-4 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 p-6"
          >
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Create New Chatbot</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Give your chatbot a name to get started.</p>
            <form onSubmit={handleCreateChatbot} className="mt-5">
              <input
                type="text"
                value={newChatbotName}
                onChange={(e) => setNewChatbotName(e.target.value)}
                placeholder="Chatbot name"
                className="w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500 placeholder:text-gray-400 transition-colors dark:text-white"
                disabled={creating}
                ref={(el) => {
                  if (el) requestAnimationFrame(() => el.focus());
                }}
              />
              <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-gray-100 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewChatbotName('');
                  }}
                  className="px-3.5 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                  disabled={creating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!newChatbotName.trim() || creating}
                  className="px-3.5 py-1.5 text-sm font-medium bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {creating ? 'Creating...' : 'Create Chatbot'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
